import { FC, useMemo, useCallback } from 'react'
import cn from 'classnames'
import moment from 'moment'
import styles from 'components/features/future/FutureLayersBar/FutureLayersBar.styl'
import themes from 'styles/base/themes.styl'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { layersSelector, networthForecastSelector } from 'components/features/future/selectors'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { cssVar } from 'helpers/styleHelpers'
import { LAYER_METAS } from 'constants/future'
import { getSvgByFilename } from 'helpers/mediaHelpers'
import SvgIcon from 'components/atoms/SvgIcon'
import { timeframeSelector, memberBirthDateSelector } from 'selectors/globalSelectors'
import { timeframeToDateRange, isDateInRange } from 'components/features/future/helpers'
import { FutureLayer } from 'components/features/future/types'
import { createGetAgeMonthPrecision } from 'helpers/dateTimeHelpers'
import { Omit } from 'interfaces'

const FUTURE_TIMEFRAME = { type: 'future' as const }

const createGetLeft = (startDate: number, endDate: number) => {
  const yearFromStartDate = moment(startDate).valueOf()
  const deltaDate = endDate - yearFromStartDate
  return (date: string) => {
    const timeLeft = moment(date).valueOf() - yearFromStartDate
    return Math.max(0, 100 * timeLeft / deltaDate)
  }
}

type TimeframeDateRange = {
  fromDate: number
  toDate: number
}

type CorrectLayer =
  & Omit<FutureLayer, 'when_month' | 'status'>
  & {
    when_month: NonNullable<FutureLayer['when_month']>
    status: 'enabled'
  }

const getCorrectLayer = (layer: FutureLayer, timeframeDateRange: TimeframeDateRange) => {
  if (layer.status !== 'enabled' || layer.when_month === null || !layer.is_achievable) {
    return null
  }

  const isInRange = isDateInRange(layer.when_month, timeframeDateRange)
  if (!isInRange) {
    return null
  }

  return layer as CorrectLayer
}


type FutureLayersBarProps = {

}
export const FutureLayersBar: FC<FutureLayersBarProps> = () => {
  const birthDate = useStoreSelector(memberBirthDateSelector)

  if (!birthDate) {
    return null
  }

  return (
    <FutureLayersBarInner birthDate={ birthDate } />
  )
}
type FutureLayersBarInnerProps =
  & FutureLayersBarProps
  & {
    birthDate: string
  }
const FutureLayersBarInner: FC<FutureLayersBarInnerProps> = (props) => {
  const { birthDate } = props

  const dispatch = useStoreDispatch()

  const openMemberLayers = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('future', {
      mode: 'member-layers',
    }))
  }, [dispatch])

  const handleSelectLayer = useCallback((uid) => {
    dispatch(modifyFloatingPaneActionCreator('future', {
      mode: 'layer-details',
      uid,
    }))
  }, [dispatch])

  const layers = useStoreSelector(layersSelector)
  const items = useStoreSelector(networthForecastSelector)
  const lastItemDate = items.length !== 0 ?
    items[items.length - 1].date :
    null

  const timeframe = useStoreSelector((state) => timeframeSelector(state, FUTURE_TIMEFRAME))

  const { getLeft, timeframeDateRange } = useMemo(() => {
    if (lastItemDate === null) {
      return {
        getLeft: null,
        timeframeDateRange: null,
      }
    }
    const { fromDate, toDate } = timeframeToDateRange(timeframe, birthDate, lastItemDate)
    return {
      getLeft: createGetLeft(fromDate, toDate),
      timeframeDateRange: { fromDate, toDate },
    }
  }, [timeframe, birthDate, lastItemDate])

  const getAge = useMemo(() => createGetAgeMonthPrecision(birthDate), [birthDate])

  const layersChildren = useMemo(() => {
    if (!items || items.length === 0 || getLeft === null || timeframeDateRange === null) {
      return null
    }

    const layersGroups = layers.reduce((acc, layer) => {
      const nextLayer = getCorrectLayer(layer, timeframeDateRange)
      if (nextLayer === null) {
        return acc
      }

      const age = getAge(nextLayer.when_month)
      let group = acc[age]
      if (!group) {
        group = acc[age] = {
          age,
          layers: [],
        }
      }
      group.layers.push(nextLayer)
      return acc
    }, {} as Record<string, { age: number, layers: CorrectLayer[]}>)

    const layersSorted = Object.values(layersGroups).sort((a, b) => a.age - b.age)

    return layersSorted.map((group) => {
      const layer = group.layers[0]
      const { slug, name, when_month, uid } = layer
      const { icon, type } = LAYER_METAS[slug]

      const startOffset = getLeft(when_month)
      const layerButton = cn(styles.layerButton, themes[type])

      if (group.layers.length > 1) {
        return (
          <div
            key={ `${ uid }_${ when_month }` }
            className={ styles.layer }
            style={
              cssVar({
                '--left': `${ startOffset }%`,
              })
            }
          >
            <button
              className={ layerButton }
              onClick={ openMemberLayers }
            >
              <i className={ styles.icon }>
                { group.layers.length }
              </i>
            </button>
          </div>
        )
      }

      return (
        <div
          key={ `${ uid }_${ when_month }` }
          className={ styles.layer }
          style={
            cssVar({
              '--left': `${ startOffset }%`,
            })
          }
        >
          <button
            className={ layerButton }
            onClick={ () => handleSelectLayer(uid) }
          >
            <i className={ styles.icon }>
              <SvgIcon src={ getSvgByFilename(icon) } />
            </i>
            <div className={ styles.text }>{ name }</div>
          </button>
        </div>
      )
    })

  }, [getAge, getLeft, handleSelectLayer, items, layers, timeframeDateRange, openMemberLayers])

  if (!layersChildren) {
    return null
  }

  return (
    <div className={ styles.FutureLayersBar }>
      <div className={ styles.layersBackground } onClick={ openMemberLayers } />
      { layersChildren }
    </div>
  )
}
